import { RefObject, useCallback, useEffect, useRef } from "react";

type ScrollOwner = "element" | "scrollbar";
type PointerPosition = { x: number; y: number };
type InteractionOwner = {
  side: ScrollOwner;
  idleMs: number | null;
  lastScrollAt: number;
};

type UseScrollSyncOptions = {
  axis: "horizontal" | "vertical";
  element: HTMLElement | null;
  scrollbarRef: RefObject<HTMLDivElement>;
};

const INFERRED_OWNER_IDLE_MS = 500;
const RELEASE_GRACE_MS = 120;

const isSameScrollPosition = (first: number, second: number) =>
  Math.abs(first - second) < 1;

const isPointInside = (element: HTMLElement, point: PointerPosition) => {
  const rect = element.getBoundingClientRect();
  return (
    point.x >= rect.left &&
    point.x <= rect.right &&
    point.y >= rect.top &&
    point.y <= rect.bottom
  );
};

const useScrollSync = ({
  axis,
  element,
  scrollbarRef,
}: UseScrollSyncOptions) => {
  const expectedEcho = useRef<Record<ScrollOwner, number | null>>({
    element: null,
    scrollbar: null,
  });
  const interactionOwner = useRef<InteractionOwner | null>(null);
  const scrollProperty = axis === "horizontal" ? "scrollLeft" : "scrollTop";

  const getOwnerAt = useCallback(
    (point: PointerPosition): ScrollOwner | null => {
      if (scrollbarRef.current && isPointInside(scrollbarRef.current, point))
        return "scrollbar";
      if (element && isPointInside(element, point)) return "element";
      return null;
    },
    [element, scrollbarRef],
  );

  const syncFromOwner = useCallback(
    (source: HTMLElement, target: HTMLElement, targetSide: ScrollOwner) => {
      const sourcePosition = source[scrollProperty];
      const targetPosition = target[scrollProperty];
      if (isSameScrollPosition(targetPosition, sourcePosition)) return;

      target[scrollProperty] = sourcePosition;

      // A write may be clamped, and only an actual movement emits a scroll event.
      const actualTargetPosition = target[scrollProperty];
      if (!isSameScrollPosition(targetPosition, actualTargetPosition))
        expectedEcho.current[targetSide] = actualTargetPosition;
    },
    [scrollProperty],
  );

  const handleScrollEvent = useCallback(
    (side: ScrollOwner) => {
      const scrollbar = scrollbarRef.current;
      if (!element || !scrollbar) return false;

      const source = side === "element" ? element : scrollbar;
      const target = side === "element" ? scrollbar : element;
      const targetSide = side === "element" ? "scrollbar" : "element";
      const sourcePosition = source[scrollProperty];

      // Writing to the target also emits a scroll event there. It is never a
      // new source, even if that event arrives after another owner scroll.
      const pendingEcho = expectedEcho.current[side];
      if (pendingEcho !== null) {
        expectedEcho.current[side] = null;
        if (isSameScrollPosition(sourcePosition, pendingEcho)) return false;
      }

      const now = performance.now();
      const activeOwner = interactionOwner.current;
      if (
        activeOwner &&
        activeOwner.idleMs !== null &&
        now - activeOwner.lastScrollAt > activeOwner.idleMs
      ) {
        interactionOwner.current = null;
      }

      if (interactionOwner.current && interactionOwner.current.side !== side) {
        // A different side cannot take over during this interaction.
        syncFromOwner(target, source, side);
        return false;
      }

      if (!interactionOwner.current) {
        interactionOwner.current = {
          side,
          idleMs: INFERRED_OWNER_IDLE_MS,
          lastScrollAt: now,
        };
      } else {
        interactionOwner.current.lastScrollAt = now;
      }

      syncFromOwner(source, target, targetSide);
      return true;
    },
    [element, scrollbarRef, scrollProperty, syncFromOwner],
  );

  useEffect(() => {
    if (!element) return;

    const updatePointer = (event: MouseEvent | PointerEvent) => {
      if (event.buttons === 0 && interactionOwner.current?.idleMs === null) {
        interactionOwner.current.idleMs = RELEASE_GRACE_MS;
        interactionOwner.current.lastScrollAt = performance.now();
      }
    };

    const handlePointerDown = (event: MouseEvent | PointerEvent) => {
      const point = { x: event.clientX, y: event.clientY };
      const side = getOwnerAt(point);
      interactionOwner.current = side
        ? { side, idleMs: null, lastScrollAt: performance.now() }
        : null;
    };

    const handleWheel = (event: WheelEvent) => {
      if (interactionOwner.current?.idleMs === null) return;
      const point = { x: event.clientX, y: event.clientY };
      const side = getOwnerAt(point);
      if (!side) return;
      interactionOwner.current = {
        side,
        idleMs: INFERRED_OWNER_IDLE_MS,
        lastScrollAt: performance.now(),
      };
    };

    const releaseOwner = () => {
      if (!interactionOwner.current) return;
      interactionOwner.current.idleMs = RELEASE_GRACE_MS;
      interactionOwner.current.lastScrollAt = performance.now();
    };

    const clearOwner = () => {
      interactionOwner.current = null;
    };

    document.addEventListener("pointermove", updatePointer, true);
    document.addEventListener("mousemove", updatePointer, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: true,
    });
    window.addEventListener("pointerup", releaseOwner, true);
    window.addEventListener("mouseup", releaseOwner, true);
    window.addEventListener("pointercancel", releaseOwner, true);
    window.addEventListener("blur", clearOwner);

    return () => {
      document.removeEventListener("pointermove", updatePointer, true);
      document.removeEventListener("mousemove", updatePointer, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("wheel", handleWheel, true);
      window.removeEventListener("pointerup", releaseOwner, true);
      window.removeEventListener("mouseup", releaseOwner, true);
      window.removeEventListener("pointercancel", releaseOwner, true);
      window.removeEventListener("blur", clearOwner);
      interactionOwner.current = null;
    };
  }, [element, getOwnerAt]);

  useEffect(() => {
    if (!element) return;
    const echoState = expectedEcho.current;

    const handleElementScroll = () => {
      handleScrollEvent("element");
    };

    echoState.element = null;
    echoState.scrollbar = null;
    element.addEventListener("scroll", handleElementScroll, { passive: true });
    return () => {
      element.removeEventListener("scroll", handleElementScroll);
      echoState.element = null;
      echoState.scrollbar = null;
    };
  }, [element, handleScrollEvent]);

  return useCallback(() => {
    return handleScrollEvent("scrollbar");
  }, [handleScrollEvent]);
};

export default useScrollSync;
